import React, { ReactNode } from "react"
import {
  Text, VStack, FormControl, Box, Checkbox as ChakraCheckbox,
} from "@chakra-ui/react"
import { Control, useWatch, useController } from "react-hook-form"
import EditorPane from "components/CanvasEditor/EditorPane"
import { useIsMixed } from "components/CanvasEditor/Inspector/MixedValues"
import Upload from "components/inputs/Upload"
import { badgeImageUploadUrl } from "api/badges"
import RadioButtons from "components/ReactHookForm/RadioButtons"
import { useBadgeTemplate } from "../../../hooks/useBadgeTemplate"
import { BadgeElementFormValues } from "../BadgeSidePanelForm"

type Props = {
  control: Control<BadgeElementFormValues>
  eventId: string
  /** Several images: no upload, which would replace them all. */
  group?: boolean
}

const BadgeImageFields = ({ control, eventId, group = false }: Props) => {
  const uploadedImageUrl = useWatch({ control, name: "url" })
  const hasUploadedImage = Boolean(uploadedImageUrl)
  const { currentTemplate } = useBadgeTemplate()

  const uploadUrl = badgeImageUploadUrl(eventId, currentTemplate?.id || "")

  // Handle grayscale filter as checkbox
  const { field: grayscaleField } = useController({
    control,
    name: "filters.grayscale",
  })
  const grayscaleMixed = useIsMixed("filters.grayscale")

  const imageFitOptions = [
    {
      label: "Maintain Aspect Ratio",
      value: "contain",
      additionalElement: (
        <AdditionalElementWrapper>
          <Text fontSize="sm" color="gray.800" lineHeight="1.4">
            Scales the image to fit inside the frame while keeping
            its original aspect ratio, with no cropping.
          </Text>
        </AdditionalElementWrapper>
      ),
    },
    {
      label: "Stretch to Fit",
      value: "fill",
      additionalElement: (
        <AdditionalElementWrapper>
          <Text fontSize="sm" color="gray.800" lineHeight="1.4">
            Scales the image to fill the frame without preserving the original
            aspect ratio, which can stretch or compress the image.
          </Text>
        </AdditionalElementWrapper>
      ),
    },
    {
      label: "Crop",
      value: "cover",
      additionalElement: (
        <AdditionalElementWrapper>
          <Text fontSize="sm" color="gray.800" lineHeight="1.4">
            Lets you adjust the visible area by dragging the
            edges of the image to hide unwanted parts.
          </Text>
        </AdditionalElementWrapper>
      ),
    },
  ]

  return (
    <VStack spacing={6} align="stretch">
      <EditorPane title={group ? "Image" : "File Upload"}>
        <VStack gap={4} alignItems="stretch" w="full">
          {!group && (
            <VStack gap={0}>
              <Upload name="url" control={control} uploadUrl={uploadUrl} />
              <Text fontSize="sm" color="gray.700">
                For best results, upload a high resolution JPG or PNG file under 5 MB
                with colors that match your printer output.
              </Text>
            </VStack>
          )}

          <ChakraCheckbox
            isChecked={grayscaleField.value === 1 && !grayscaleMixed}
            isIndeterminate={grayscaleMixed}
            onChange={(e) => grayscaleField.onChange(e.target.checked ? 1 : 0)}
            isDisabled={!group && !hasUploadedImage}
            spacing={3}
          >
            <Text>Convert to Black & White</Text>
          </ChakraCheckbox>
        </VStack>
      </EditorPane>

      <EditorPane title="Image Resizing">
        <FormControl>
          <RadioButtons
            name="fit"
            control={control}
            options={imageFitOptions}
            spacing={3}
            mb={0}
          />
        </FormControl>
      </EditorPane>

      {/* Commented out for now, but very possible it comes back */}
      {/* <ImageFiltersSection control={control} /> */}
    </VStack>
  )
}

const AdditionalElementWrapper = ({ children }: { children: ReactNode }) => (
  <Box w="100%" pl={7} pr={6} mt={-2} mb={2} whiteSpace="normal" overflowWrap="break-word">
    {children}
  </Box>
)

export default BadgeImageFields

// const ImageFiltersSection = ({ control }: { control: Control<BadgeElementFormValues> }) => (
//   <EditorPane title="Filters">
//     <VStack spacing={2}>
//       {IMAGE_FILTER_CONTROLS.map((filter) => (
//         <PropertyRow key={filter.name} label={filter.label}>
//           <FilterControl
//             name={filter.name}
//             control={control}
//             min={filter.min}
//             max={filter.max}
//             step={filter.step}
//             defaultValue={filter.defaultValue}
//           />
//         </PropertyRow>
//       ))}
//     </VStack>
//   </EditorPane>
// )
